import invariant from "invariant";
import {
  ActionTypes,
  DoneInvokeEvent,
  ErrorPlatformEvent,
  assign,
  createMachine,
} from "xstate";
import { raise } from "xstate/lib/actions";

import { StripeSdk } from "@zapier/stripe";

import { invoicePreviewQuery_account_invoicePreview_paymentIntent as PaymentIntentType } from "../../graphql-types";
import {
  confirmPaymentIntent,
  isStripeError,
  logToSentry,
  redirectToLogin,
} from "../../utils";

enum State {
  idle = "idle",
  init = "init",
  errorInitializing = "errorInitializing",
  waitForCheckout = "waitForCheckout",
  checkAuthenticationStatus = "checkAuthenticationStatus",
  confirmPaymentIntent = "confirmPaymentIntent",
  errorConfirmingPaymentIntent = "errorConfirmingPaymentIntent",
  submit = "submit",
  errorSubmitting = "errorSubmitting",
  done = "done",
}

enum Event {
  resetWithPaymentIntent = "resetWithPaymentIntent",
  init = "init",
  submit = "submit",
  redirect = "redirect",
  retry = "retry",
  cancel = "cancel",
}

enum Tag {
  loading = "loading",
  submittable = "submittable",
}

export type Context = {
  isSdkInitialized: boolean;
  paymentIntent?: PaymentIntentType | null;
  error?: stripe.Error | Error;
};

export type EventObject =
  | {
      type: Event.resetWithPaymentIntent;
      paymentIntent: PaymentIntentType | null | undefined;
    }
  | { type: Event.init }
  | { type: Event.submit }
  | { type: Event.redirect }
  | { type: Event.retry }
  | { type: Event.cancel };

const isErrorPlatformEvent = (event: any): event is ErrorPlatformEvent =>
  event.type.startsWith(ActionTypes.ErrorPlatform);

const isDoneInvokeEvent = <TData>(
  event: any
): event is DoneInvokeEvent<TData> => "data" in event;

const paymentIntentsMachine = createMachine<Context, EventObject>(
  {
    id: "paymentIntentsMachine",
    initial: State.idle,
    context: {
      isSdkInitialized: false,
      paymentIntent: undefined,
      error: undefined,
    },
    states: {
      [State.idle]: {
        tags: Tag.submittable,
        on: {
          [Event.init]: [
            {
              target: State.waitForCheckout,
              cond: "hasIntentAndSdkIsInitialized",
            },
            {
              target: State.init,
              cond: "hasPaymentIntent",
            },
            { target: State.idle },
          ],
          submit: { target: State.submit, cond: "paymentIntentNotRequired" },
        },
      },

      [State.init]: {
        tags: Tag.loading,
        invoke: {
          src: "eagerLoadStripeSdk",
          onDone: {
            target: State.waitForCheckout,
            actions: "setIsSdkInitialized",
          },
          onError: State.errorInitializing,
        },
      },

      [State.errorInitializing]: {
        entry: ["logInitializationError"],
        type: "final",
      },

      [State.waitForCheckout]: {
        tags: Tag.submittable,
        on: {
          [Event.submit]: State.checkAuthenticationStatus,
        },
      },

      /**
       * Make sure the customer is authenticated before allowing them to proceed.
       * If they are not authenticated, we redirect them to login before they
       * can pay us or try to checkout.
       */
      [State.checkAuthenticationStatus]: {
        tags: Tag.loading,
        // @ts-ignore - types are wonky here
        invoke: {
          src: "queryAuthenticationStatus",
          onDone: [
            { target: State.confirmPaymentIntent, cond: "userIsAuthenticated" },
            /**
             * Normally, we could just fire an action here, but this action ends
             * up competing with another event to reset the machine (see resetWithPaymentIntent event below).
             * In order to combat this race condition, we raise the event here
             * to prioritize it in the event queue of the state machine.
             */
            { actions: raise("redirect") },
          ],
        },
        on: {
          [Event.redirect]: { actions: ["redirectToLogin"] },
        },
      },

      [State.confirmPaymentIntent]: {
        tags: Tag.loading,
        invoke: {
          src: "confirmPaymentIntent",
          onDone: State.submit,
          onError: {
            target: State.errorConfirmingPaymentIntent,
            actions: [
              assign({
                error: (_context, event) => event.data,
              }),
            ],
          },
        },
      },

      [State.errorConfirmingPaymentIntent]: {
        tags: Tag.submittable,
        entry: ["maybeLogConfirmationError"],
        on: {
          [Event.retry]: State.confirmPaymentIntent,
          [Event.submit]: State.confirmPaymentIntent,
        },
      },

      [State.submit]: {
        tags: Tag.loading,
        invoke: {
          src: "submitPayment",
          onDone: State.done,
          onError: {
            target: State.errorSubmitting,
            actions: [
              assign({
                error: (_context, event) => event.data,
              }),
            ],
          },
        },
      },

      // Errors that happen when submitting should get picked up by our changePlanError alert in ChangePlan.tsx
      [State.errorSubmitting]: {
        tags: Tag.submittable,
        on: {
          [Event.submit]: State.submit,
        },
      },

      [State.done]: {
        type: "final",
      },
    },

    on: {
      [Event.resetWithPaymentIntent]: {
        target: "#paymentIntentsMachine",
        actions: ["resetMachine"],
      },
    },
  },
  {
    guards: {
      hasPaymentIntent: context => Boolean(context.paymentIntent),
      paymentIntentNotRequired: context => !context.paymentIntent,
      hasIntentAndSdkIsInitialized: context =>
        Boolean(context.paymentIntent) && context.isSdkInitialized,
      /**
       * This guard needs to be called in the context of a service being invoked
       * that returns {isAuthenticated: boolean}. The only instance of this being
       * called so far is during the `checkAuthenticationStatus` state, which
       * invokes the isAuthenticatedQuery Apollo query.
       */
      userIsAuthenticated: (context, event) => {
        invariant(
          isDoneInvokeEvent<{ isAuthenticated: boolean }>(event),
          "userIsAuthenticated guard requires an invoked service with a result containing `isAuthenticated` (`data` is missing in event)"
        );

        return event.data.isAuthenticated;
      },
    },
    actions: {
      logInitializationError: (_context, event) => {
        if (isErrorPlatformEvent(event))
          logToSentry(`paymentIntentsMachine.${event.type}`, event.data);
      },

      maybeLogConfirmationError: context => {
        if (
          isStripeError(context.error) &&
          // Card errors are errors specific to card details or errors processing a user's card, declines, etc
          context.error.type !== "card_error"
        ) {
          logToSentry(`paymentIntentsMachine.confirm.error`, context.error);
        }
      },

      setIsSdkInitialized: assign({
        isSdkInitialized: true,
      }),

      redirectToLogin,

      resetMachine: assign((_context, event: EventObject) => {
        return event.type === Event.resetWithPaymentIntent
          ? {
              paymentIntent: event.paymentIntent,
              error: undefined,
            }
          : {};
      }),
    },
    services: {
      eagerLoadStripeSdk: async () => {
        const result = await StripeSdk.get();
        if (result.isErr()) throw result.error;
      },

      confirmPaymentIntent: async context => {
        invariant(
          context.paymentIntent,
          "We must have a payment intent to confirm."
        );
        const result = await confirmPaymentIntent(context.paymentIntent);
        if (result.isErr()) throw result.error;
        return result.value;
      },
    },
  }
);

export { paymentIntentsMachine, State, Event, Tag };
